import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { UpsertManufacturer } from "../../http/manufacturers/types";
import ModalDialog from "../ui/ModalDialog";
import ManufacturerForm, { ManufacturerFormBody } from "./ManufacturerForm";

type EditManufacturerDialogProps = {
  manufacturerId: string;
  onClose: () => void;
};

const EditManufacturerDialog: FC<EditManufacturerDialogProps> = ({
  manufacturerId: manufacturerId,
  onClose,
}: EditManufacturerDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const manufacturersApi = useManufacturersApi();

  const manufacturerSwr = manufacturersApi.getAdminManufacturer(manufacturerId, aborter.current.signal);
  const [manufacturer, isInitialized] = useDataInitializer(manufacturerSwr.data, manufacturerSwr.error);

  useEffect(() => {
    manufacturerId && manufacturerSwr.trigger();
  }, [manufacturerId]);

  useEffect(() => {
    manufacturerSwr.error && dispatch(showErrorNotification(i18n, manufacturerSwr.error));
  }, [manufacturerSwr.error]);

  const onSubmit = async (formData: ManufacturerFormBody): Promise<void> => {
    if (manufacturer?.id) {
      const body: UpsertManufacturer = {
        name: formData.name,
        currentVersionNumber: manufacturer.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await manufacturersApi.updateAdminManufacturer(manufacturerId, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.manufacturers.edit}>
      <ManufacturerForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.manufacturers.update || isSubmitting || !manufacturer?.id}
        isLoading={!isInitialized}
        manufacturer={manufacturer}
      />
    </ModalDialog>
  );
};

export default EditManufacturerDialog;
